import { getInvoiceDetailsCardInfo } from "../invoice-master/invoice-helper";
import { RootState } from "src/context/store";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "src/components";
import { invoiceActions } from "src/context/invoice-slice";
import { fetchEscalatedInvoicesData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import useInvoiceSearchGate, { INVOICE_SEARCH_PROMPT } from "src/hooks/useInvoiceSearchGate";
import { useCallback } from "react";
import InvoiceApprovalDetailCard from "../invoice-homepage/InvoiceApprovalDetailCard";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";

interface InvoiceApprovalEscalatedTabProps {
  searchCategory: string;
  searchValue: string;
}

const InvoiceApprovalEscalatedTab = ({ searchCategory, searchValue }: InvoiceApprovalEscalatedTabProps) => {
  const { invoiceApprovalEscalatedList, InvoiceApprovalFilters, invoiceAnalysisData } = useSelector((state: RootState) => state.invoice);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { canFetch, awaitingCriteria } = useInvoiceSearchGate(InvoiceApprovalFilters, searchValue); // M10-16144

  const { hasPermission: hasViewDetailsPermission, loading: detailsPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["VIEW DETAILS"].module,
    action: fineGrainedPermissionsKeys.INVOICE["VIEW DETAILS"].action,
  });

  const { hasPermission: hasPermissionReassign, loading: reassignPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.REASSIGN.module,
    action: fineGrainedPermissionsKeys.INVOICE.REASSIGN.action,
  });
  const { hasPermission: hasPermissionPutOnHold, loading: putOnHoldPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["PUT ON HOLD"].module,
    action: fineGrainedPermissionsKeys.INVOICE["PUT ON HOLD"].action,
  });
  const { hasPermission: hasPermissionReject, loading: rejectPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.REJECT.module,
    action: fineGrainedPermissionsKeys.INVOICE.REJECT.action,
  });
  const { hasPermission: hasPermissionEscalate, loading: escalatePermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.ESCALATE.module,
    action: fineGrainedPermissionsKeys.INVOICE.ESCALATE.action,
  });
  const { hasPermission: hasPermissionSetPriority, loading: setPriorityPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["SETPRIORITY/REMOVEPRIORITY"].module,
    action: fineGrainedPermissionsKeys.INVOICE["SETPRIORITY/REMOVEPRIORITY"].action,
  });

  const { hasPermission: hasPermissionReEntry, loading: reEntryPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["REENTRY"].module,
    action: fineGrainedPermissionsKeys.INVOICE["REENTRY"].action,
  });
  const { hasPermission: hasPermissionStockistAck, loading: stockistAckPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["STOCKIST_ACK"].module,
    action: fineGrainedPermissionsKeys.INVOICE["STOCKIST_ACK"].action,
  });

  const searchParams = useMemo(
    () => ({
      company_code: InvoiceApprovalFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceApprovalFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceApprovalFilters?.dateRange?.from,
      endDate: InvoiceApprovalFilters?.dateRange?.to,
      assignee_id: InvoiceApprovalFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceApprovalFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceApprovalFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceApprovalFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceApprovalFilters?.sorting?.[0]?.key,
      fuid_status_id: InvoiceApprovalFilters?.status?.length ||0 > 0
        ? InvoiceApprovalFilters?.status?.map(item => item.fuid_status_id).join(",") || "6,8"
        : (searchValue && searchValue.length > 0 ? "" : "6,8"),
      page_size: 10,
      page_number: 1,
      screen: "Approval",
      dateType: InvoiceApprovalFilters?.dateType?.[0]?.key || "",
      uploadType: InvoiceApprovalFilters?.uploadType?.[0]?.key,
      moduleType: InvoiceApprovalFilters?.moduleType?.[0]?.key,
      order_mode: InvoiceApprovalFilters?.orderMode?.[0]?.key,
      cndn_remarks_code_int: InvoiceApprovalFilters?.orderType?.[0]?.key,
      entered_by: InvoiceApprovalFilters?.enteredBy?.map((item) => item.entered_by)?.join(",") || "",
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
    }),
    [InvoiceApprovalFilters, searchCategory, searchValue]
  );

  const fetchEscalatedData = useCallback(
    async () => {
      setIsLoading(true);
      dispatch(invoiceActions.setInvoiceApprovalData([]));
      try {
        const allInvoicesRes = await fetchEscalatedInvoicesData(searchParams);
        dispatch(invoiceActions.setInvoiceApprovalEscaltedList(allInvoicesRes?.data?.data?.invoiceData || []));
        if (allInvoicesRes?.data?.data) {
          dispatch(invoiceActions.setInvoiceAnalysisData(
            {
              entry: {
                "All Invoices": invoiceAnalysisData?.entry?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.entry?.["Priority Tasks"] || 0,
                "Escalated": invoiceAnalysisData?.entry?.["Escalated"] || 0,
                "Rejection Review": 0,
                "Feedback from Ext": 0,
              },
              approval: {
                "All Invoices": invoiceAnalysisData?.approval?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.approval?.["Priority Tasks"] || 0,
                "Escalated": allInvoicesRes?.data?.data?.total || 0,
              }
            }
          ));
        }
        setPage(2);
        setHasMore(true);
        toast({ description: "Invoice data fetched successfully", variant: "default" });
      } catch (error) {
        console.error("Error fetching invoices data:", error);
        toast({ description: "Failed to fetch invoice data", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, searchParams]
  );

  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: page, skip_counts: true }; // M10-16144: page N reads only invoiceData
      const res = await fetchEscalatedInvoicesData(nextPageParams);
      const newData = res?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }
      dispatch(invoiceActions.setInvoiceApprovalEscaltedList([...(invoiceApprovalEscalatedList || []), ...newData]));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more invoices:", error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // M10-16144: roles outside the default-load list wait for a search/filter before fetching
    if (!canFetch) {
      dispatch(invoiceActions.setInvoiceApprovalEscaltedList([]));
      setHasMore(false);
      return;
    }
    fetchEscalatedData();
  }, [fetchEscalatedData, canFetch]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        {" "}
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-10 w-full">
      {invoiceApprovalEscalatedList?.length === 0 && (
        <div className="flex justify-center items-center h-[20vh]">
          <p className="text-lg text-gray-500">{awaitingCriteria ? INVOICE_SEARCH_PROMPT : "No invoices found"}</p>
        </div>
      )}

      <div id="scrollableDiv" className="h-screen overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <InfiniteScroll
          dataLength={invoiceApprovalEscalatedList?.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={""}
          scrollableTarget="scrollableDiv"
          endMessage={awaitingCriteria ? null : <div className="py-4 text-center text-gray-500">No more invoices to load</div>}
        >
          <ul>
            {invoiceApprovalEscalatedList?.map((data: any, index: number) => (
              <li className="my-5 rounded-xl border shadow-sm border-gray-200" key={index}>
                <InvoiceApprovalDetailCard
                  status={data?.fileStatusHdr?.fuid_status_desc}
                  data={getInvoiceDetailsCardInfo(data, false)} //M10-15065
                  checkDataType={false}
                  invoiceData={data}
                  viewDetails={true}
                  index={index}
                  approvalScreen={true}
                  isEsalated={false}
                  hasViewDetailsPermission={hasViewDetailsPermission}
                  detailsPermissionLoading={detailsPermissionLoading}
                  hasPermissionReassign={hasPermissionReassign}
                  reassignPermissionLoading={reassignPermissionLoading}
                  hasPermissionPutOnHold={hasPermissionPutOnHold}
                  putOnHoldPermissionLoading={putOnHoldPermissionLoading}
                  hasPermissionReject={hasPermissionReject}
                  rejectPermissionLoading={rejectPermissionLoading}
                  hasPermissionEscalate={hasPermissionEscalate}
                  escalatePermissionLoading={escalatePermissionLoading}
                  hasPermissionSetPriority={hasPermissionSetPriority}
                  setPriorityPermissionLoading={setPriorityPermissionLoading}
                  hasPermissionReEntry={hasPermissionReEntry}
                  reEntryPermissionLoading={reEntryPermissionLoading}
                  hasPermissionStockistAck={hasPermissionStockistAck}
                  stockistAckPermissionLoading={stockistAckPermissionLoading}
                />
              </li>
            ))}
          </ul>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default InvoiceApprovalEscalatedTab;
